/**
 * @lines98/client/components/gem-pool-hud — лента запаса камней: 7 счётчиков по цветам
 * (`meta-gems-resource.md` T2 §5.1 / §5.2, AC-G8).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки» + §5.2 «можно
 * переиспользовать тот же модуль камней-HUD»): пул камней — видимый накапливаемый запас
 * (как золото/осколки). Игрок видит его в шапке Мастерской (§5.1) И в карточке детали
 * зверя (§5.2 — сколько доступно к вставке). ЕДИНЫЙ модуль, монтируемый в обоих местах,
 * НЕ копия по экранам: 7 ячеек [иконка цвета + число]. Нулевой цвет (`pool[X]=0`) —
 * приглушён (тусклое гнездо + «0»), НЕ скрыт (игрок видит, каких цветов не хватает, §5.1.3).
 *
 * **Server-truth (AC-G11):** лента — ПРОЕКЦИЯ массива `gemPool` из `GET /meta/state`.
 * Ничего не пересчитывает; `setPool` ставит server-truth снапшот.
 *
 * **Раскладка (§5.1.2 тактический минимум):** лента — flex с `wrap` ВНУТРИ своего региона
 * (`auto`-row), не страничный scroll. На узком touch portrait 7 камней переносятся на
 * 2-ю строку (числа must-visible — P1 > плотность P4). Размеры — токенами от `--cell-size`
 * (= иконка currency-hud), без magic-px/капов (§4.2 / AC-G14).
 *
 * SCOPE: рендер 7 счётчиков пула + dim нулей + опц. подсветка одного цвета (pulse −1/+1
 * после мутации). NOT IN SCOPE: мутации (insert/forge — экран зовёт meta-api + ресинк).
 *
 * Cross-references:
 *   - meta-gems-resource.md §5.1/§5.2 (пул-HUD), §3 I-G6, §8 AC-G8/AC-G11.
 *   - components/meta-icons.ts (`gemGlyph` / `gemEmptyGlyph`).
 *   - lib/currency-hud.ts (HUD-модуль образец: mount → handle, co-located CSS).
 */

import './gem-pool-hud.css';

import {
  GEM_POOL_COLORS,
  GEM_NAME_BY_COLOR,
  GEM_RU_NAME_BY_COLOR as GEM_RU_NAME,
} from '@lines98/shared/config/meta';
import type { ColorIndex } from '@lines98/shared/types/game';

import { formatRuNumber } from '../lib/format.js';
import { t } from '../lib/i18n/index.js';
import { gemGlyph } from './meta-icons.js';

export interface GemPoolHudOptions {
  /**
   * Стартовый снапшот пула (массив 1..7; индекс 0 sentinel). Опц. — без него лента
   * стартует с нулей (приглушённых), пока экран не поставит server-truth.
   */
  readonly pool?: readonly number[];
  /** Доп. класс(ы) для контекста surface'а (HUD Мастерской / карточка детали). */
  readonly className?: string;
}

export interface GemPoolHudHandle {
  readonly element: HTMLElement;
  /** Поставить server-truth снапшот пула (массив 1..7). Перерисовывает числа + dim нулей. */
  setPool(pool: readonly number[]): void;
  /** Короткий «пульс» у счётчика цвета X (transform/opacity) — акцент изменения. */
  pulse(color: number): void;
  /** Размонтировать: снять DOM. */
  unmount(): void;
}

interface GemCell {
  readonly color: ColorIndex;
  readonly root: HTMLElement;
  readonly iconHost: HTMLElement;
  readonly valueEl: HTMLElement;
}

/**
 * Смонтировать ленту запаса камней в `host`. 7 счётчиков [иконка цвета + число],
 * нулевые — приглушены (но видны). Возвращает handle для server-truth обновления.
 */
export function mountGemPoolHud(host: HTMLElement, opts: GemPoolHudOptions = {}): GemPoolHudHandle {
  const root = document.createElement('div');
  root.className = 'gem-pool-hud';
  root.setAttribute('role', 'group');
  root.setAttribute('aria-label', t('game.currency.gemPoolAria'));
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));

  const cells: GemCell[] = [];
  for (let c = 1; c <= GEM_POOL_COLORS; c++) {
    const color = c as ColorIndex;
    const cell = document.createElement('div');
    // `gem-pool-item` — стабильный публичный хук одной ячейки-счётчика (один на цвет,
    // 7 штук). Vis-тесты считают камни именно по нему; визуальные правила навешены на
    // `gem-pool-cell` (внутренний класс, не переименовываем — single source). Обе живут
    // на ОДНОМ узле (alias), без отдельной разметки.
    cell.className = 'gem-pool-cell gem-pool-item';
    cell.dataset.color = String(color);

    const iconHost = document.createElement('span');
    iconHost.className = 'gem-pool-icon';
    iconHost.setAttribute('aria-hidden', 'true');
    // Камень-картинку создаём ОДИН раз (цвет ячейки фиксирован) → НЕ пересоздаём в setPool.
    // Постоянный warm `<img>` переживает ре-рендеры (смена сессии / обновление пула) без
    // повторного создания → нет «картинка слетела и подгрузилась» (owner-bug 2026-06-11:
    // возврат из фоновой вкладки + разлогин → setPool рекреировал `<img>` на остывшем
    // декоде → мелькание). Меняется только число + приглушение (см. setPool).
    iconHost.innerHTML = gemGlyph(color);

    const value = document.createElement('span');
    value.className = 'gem-pool-value';
    value.textContent = '0';

    cell.append(iconHost, value);
    root.appendChild(cell);
    cells.push({ color, root: cell, iconHost, valueEl: value });
  }

  host.appendChild(root);

  function setPool(pool: readonly number[]): void {
    for (const cell of cells) {
      const count = Number.isFinite(pool[cell.color]) ? Math.max(0, Math.floor(pool[cell.color]!)) : 0;
      const empty = count === 0;
      // Камень-картинка ПОСТОЯННА (создана раз при mount) — здесь НЕ пересоздаём `<img>`
      // (иначе ре-рендер при смене сессии/пула рекреировал бы картинку → мелькание на
      // остывшем декоде). Меняем только число + приглушение ячейки (.gem-pool-cell--empty).
      // owner-directive 2026-06-11: всегда настоящий камень (даже при 0), пустоту показывает
      // счётчик «0» + dim; силуэт-«тень» в хранилище убран.
      cell.valueEl.textContent = formatRuNumber(count);
      cell.root.classList.toggle('gem-pool-cell--empty', empty);
      cell.root.setAttribute('aria-label', `${GEM_RU_NAME[cell.color]}: ${count}`);
      // Имя камня (config-канон) в data — для UX-контекста/тестов, без magic-строк.
      cell.root.dataset.gem = GEM_NAME_BY_COLOR[cell.color] ?? '';
    }
  }

  // Стартовый рендер (снапшот или нули).
  setPool(opts.pool ?? []);

  return {
    element: root,
    setPool,
    pulse(color: number): void {
      const cell = cells.find((x) => x.color === color);
      if (!cell) return;
      // Перезапуск CSS-анимации: снять → reflow → навесить (только transform/opacity).
      cell.root.classList.remove('gem-pool-cell--pulse');
      void cell.root.offsetWidth;
      cell.root.classList.add('gem-pool-cell--pulse');
    },
    unmount(): void {
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}
